import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

export const DEFAULT_BRAND = {
  tone: 'friendly and professional',
  max_sentences: 3,
  avoid_words: ['simply', 'just', 'easy', 'obviously', 'clearly'],
  custom_instructions: 'Use active voice. Reference the product as "Flow" not "the software" or "the app".',
  custom_tone: '',
};

const BrandContext = createContext({
  brand: DEFAULT_BRAND,
  setBrand: () => {},
  ready: false,
});

export function BrandProvider({ children }) {
  const [brand, setBrandState] = useState(DEFAULT_BRAND);
  const [ready, setReady] = useState(false);
  const saveTimer = useRef(null);

  // Initial load: DB first, fall back to legacy localStorage, then write-through.
  useEffect(() => {
    let cancelled = false;
    const readLocal = () => {
      try {
        const raw = localStorage.getItem('gp_brand');
        return raw ? JSON.parse(raw) : null;
      } catch { return null; }
    };

    api.get('/brand/voice')
      .then(r => {
        if (cancelled) return;
        const data = r.data || {};
        const local = readLocal();
        if (local && typeof local === 'object') {
          // Legacy localStorage wins once during migration, then gets written to DB.
          const merged = { ...DEFAULT_BRAND, ...data, ...local };
          setBrandState(merged);
          api.put('/brand/voice', merged).catch(() => {});
          try { localStorage.removeItem('gp_brand'); } catch {}
        } else {
          setBrandState({ ...DEFAULT_BRAND, ...data });
        }
      })
      .catch(() => {
        const local = readLocal();
        if (!cancelled && local) setBrandState({ ...DEFAULT_BRAND, ...local });
      })
      .finally(() => { if (!cancelled) setReady(true); });

    return () => { cancelled = true; };
  }, []);

  const setBrand = useCallback((updated) => {
    setBrandState(updated);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put('/brand/voice', updated).catch(() => {});
    }, 500);
  }, []);

  return (
    <BrandContext.Provider value={{ brand, setBrand, ready }}>
      {children}
    </BrandContext.Provider>
  );
}

export const useBrand = () => useContext(BrandContext);
